import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { DataTable } from 'grommet';
import { Spinner } from 'grommet';
import { Button } from 'grommet';
import { Share } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { AddBankAccountModal } from './UpdateStripeBankAccountModal';
import { ClientService } from '/src/api';
import { BankAccount, StripeGlobalPayoutsService } from '/src/api/services/StripeGlobalPayoutsService';
import { AddButton, Box, Card, CardBody, Input, Text } from '/src/components';
import { AddRecipientModal } from '/src/components/admin/AddRecipientModal';
import { DataTableHeader } from '/src/components/common';
import { config } from '/src/config';
import { useClientStore, useUserStore } from '/src/context';
import { CountryId } from '/src/lib/types';
import { isValidEmail } from '/src/utils';

export interface StripeEmailHandle {
  validate: () => boolean;
  save: () => Promise<void>;
}

interface ExtendedBankingInputProps {
  onEmailChange?: (hasChanges: boolean) => void;
  emailUpdateRef?: React.MutableRefObject<StripeEmailHandle | null>;
  clientId?: number;
  stripeAccountId?: string;
}

export const ClientStripeBankingInput: React.FC<ExtendedBankingInputProps> = observer(
  ({ clientId, onEmailChange, emailUpdateRef }) => {
    const { t } = useTranslation();
    const clientStore = useClientStore();
    const userStore = useUserStore();
    const isCanada = clientStore.selectedClient?.hq_address_region?.country_id === CountryId.Canada;
    const [payoutMethods, setPayoutMethods] = useState<BankAccount[]>([]);
    const [loading, setLoading] = useState(false);
    const [showAddBankModal, setShowAddBankModal] = useState(false);
    const [email, setEmail] = useState('');
    const [originalEmail, setOriginalEmail] = useState('');
    const [stripeAccountId, setStripeAccountId] = useState<string | null>(null);
    const [showAddRecipientModal, setShowAddRecipientModal] = useState(false);
    const [redirecting, setRedirecting] = useState(false);
    const [emailError, setEmailError] = useState('');

    const fetchData = useCallback(async () => {
      if (!clientId || !clientStore.selectedClient) return;

      setLoading(true);
      try {
        const accountInfo = await StripeGlobalPayoutsService.getAccount(clientId);
        const stripeId = clientStore.selectedClient.stripe_global_payouts_account_id;

        setEmail(accountInfo.email || '');
        setOriginalEmail(accountInfo.email || '');

        if (!stripeId) {
          setPayoutMethods([]);
          setEmail('');
          setOriginalEmail('');
          setStripeAccountId(null);
          return;
        }

        setStripeAccountId(stripeId);
        setPayoutMethods(accountInfo.bank_accounts || []);
      } catch (err) {
        console.error('Error fetching Stripe account data:', err);
        setPayoutMethods([]);
      } finally {
        setLoading(false);
      }
    }, [clientId, clientStore.selectedClient]);

    useEffect(() => {
      fetchData();
    }, [fetchData]);

    useEffect(() => {
      if (onEmailChange) {
        const hasChanges = email !== originalEmail;
        onEmailChange(hasChanges);
      }
    }, [email, originalEmail, onEmailChange]);

    useEffect(() => {
      if (emailUpdateRef) {
        emailUpdateRef.current = { validate: validateEmail, save: handleEmailUpdate };
      }
    }, [emailUpdateRef, email, clientId]);

    const pxToRem = (px: string) => {
      return `${parseInt(px) / 16}rem`;
    };

    const validateEmail = () => {
      const trimmedEmail = email.trim();
      if (!trimmedEmail) {
        setEmailError('Email is required');
        return false;
      }
      if (!isValidEmail(trimmedEmail)) {
        setEmailError('Must be a valid email address');
        return false;
      }
      return true;
    };

    const handleEmailUpdate = async () => {
      if (!clientId) return;

      const result = await StripeGlobalPayoutsService.updateEmail(clientId, {
        email: email.trim(),
      });

      setOriginalEmail(result.email);
    };

    const handleCompleteBankingDetails = async () => {
      if (!clientId || !validateEmail()) return;

      const stripeTab = window.open('', '_blank');
      if (stripeTab) {
        stripeTab.opener = null;
      }

      try {
        setRedirecting(true);
        const result = await StripeGlobalPayoutsService.createOnboardingLink(clientId, email.trim());

        if (!result.url) {
          throw new Error('No onboarding URL returned');
        }

        if (stripeTab) {
          stripeTab.location.href = result.url;
        } else {
          window.location.assign(result.url);
        }
      } catch (error) {
        stripeTab?.close();
        console.error('Error creating onboarding link:', error);
        toast.error('Failed to start banking setup');
      } finally {
        setRedirecting(false);
      }
    };

    return (
      <Box gap="2rem">
        {loading ? (
          <Box align="center" justify="center" pad="large">
            <Spinner size="medium" />
          </Box>
        ) : (
          <>
            <Box gap="1rem" fill="horizontal">
              <Box gap="1rem">
                {stripeAccountId ? (
                  <Box gap="1rem">
                    <Box direction="row" align="center" gap="small">
                      <Text color="dark-1" size="large" weight={300}>
                        {t('client:accountDetails')}
                      </Text>
                      {userStore.isFuseAdmin && !!config.stripe.platformAccountId && (
                        <Button
                          pad="xsmall"
                          icon={<Share size="16px" />}
                          href={`https://dashboard.stripe.com/${config.stripe.platformAccountId}/global-payouts/recipients/${stripeAccountId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          tip="Stripe Account"
                        />
                      )}
                    </Box>

                    <Input
                      name="display_name"
                      label={t('client:displayName')}
                      placeholder={t('client:noDisplayName')}
                      flex="grow"
                      value={clientStore.selectedClient?.name || ''}
                      disabled
                    />

                    <Box direction="row" gap="medium" align="end">
                      <Input
                        name="contact_email"
                        label={t('client:contactEmail')}
                        placeholder={t('client:enterEmailAddress')}
                        type="email"
                        flex="grow"
                        value={email}
                        setValue={(value: string) => {
                          setEmail(value);
                          if (emailError) {
                            setEmailError('');
                          }
                        }}
                        error={emailError}
                        required
                      />
                    </Box>
                  </Box>
                ) : (
                  <Box align="center" justify="center" pad="medium" background="light-6" round="small" gap="medium">
                    <Text size="large" textAlign="center">
                      {t('client:noStripeAccountConnected')}
                    </Text>
                    {isCanada ? (
                      <Text size="medium" textAlign="center" color="dark-3">
                        {t('client:stripeUnavailableForCanada')}
                      </Text>
                    ) : (
                      <AddButton label={t('client:addStripeDetails')} onClick={() => setShowAddRecipientModal(true)} />
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            {stripeAccountId ? (
              <Box gap="1rem" margin={{ top: '2rem' }}>
                <Box direction="row" align="center" justify="between">
                  <Text color="dark-1" size="large" weight={300}>
                    {t('client:linkedBankAccount')}
                  </Text>
                  <Box direction="row" gap="small">
                    {payoutMethods.length === 0 && userStore.isClientAdmin && (
                      <AddButton
                        label={t('client:completeBankingDetails')}
                        onClick={handleCompleteBankingDetails}
                        disabled={redirecting}
                      />
                    )}
                    {userStore.isFuseAdmin && (
                      <AddButton
                        label={t('client:updateBankAccount')}
                        onClick={() => clientId && setShowAddBankModal(true)}
                      />
                    )}
                  </Box>
                </Box>
                <Card>
                  <CardBody pad="none" gap="none">
                    <Box elevation="small">
                      <DataTable
                        pad={{ horizontal: '1.5rem' }}
                        columns={[
                          {
                            property: 'bank_name',
                            size: '30%',
                            header: <DataTableHeader title={t('client:bankName')} />,
                            sortable: false,
                            render: (method: BankAccount) => (
                              <Box margin={{ vertical: pxToRem('14px') }}>
                                <Text size="medium" fontFamily="Lato, sans-serif">
                                  {method.bank_account?.bank_name || ''}
                                </Text>
                              </Box>
                            ),
                          },
                          {
                            property: 'routing_number',
                            size: '25%',
                            header: <DataTableHeader title={t('client:routingNumber')} />,
                            sortable: false,
                            render: (method: BankAccount) => (
                              <Box margin={{ vertical: pxToRem('14px') }}>
                                <Text size="medium" fontFamily="Lato, sans-serif">
                                  {method.bank_account?.routing_number || ''}
                                </Text>
                              </Box>
                            ),
                          },
                          {
                            property: 'last4',
                            size: '15%',
                            header: <DataTableHeader title={t('client:last4')} />,
                            sortable: false,
                            render: (method: BankAccount) => (
                              <Box margin={{ vertical: pxToRem('14px') }}>
                                <Text size="medium" fontFamily="Lato, sans-serif">
                                  {method.bank_account?.last4 || ''}
                                </Text>
                              </Box>
                            ),
                          },
                        ]}
                        data={payoutMethods}
                        background={['light-6', 'white']}
                      />
                    </Box>
                  </CardBody>
                </Card>
              </Box>
            ) : null}
          </>
        )}
        {clientId && (
          <>
            <AddBankAccountModal
              isVisible={showAddBankModal}
              setIsVisible={setShowAddBankModal}
              clientId={clientId}
              onBankAccountAdded={async () => {
                fetchData();
              }}
            />
            <AddRecipientModal
              isVisible={showAddRecipientModal}
              setIsVisible={setShowAddRecipientModal}
              clientId={clientId}
              clientName={clientStore.selectedClient?.name}
              client={clientStore.selectedClient ?? undefined}
              onRecipientAdded={async () => {
                if (clientId && clientStore.selectedClient) {
                  const updatedClient = await ClientService.get({ id: clientId });
                  clientStore.setSelectedClient(updatedClient);
                  fetchData();
                }
              }}
            />
          </>
        )}
      </Box>
    );
  }
);
